// gallery.js
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        // If there are no thumbnails, we still want slideChanged to play media
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              mediaToSwitch
                .querySelector('button')
                .addEventListener(
                  'click',
                  this.setActiveMedia.bind(
                    this,
                    mediaToSwitch.dataset.target,
                    false,
                    true
                  )
                );
            });
        }

        if (
          this.dataset.desktopLayout === 'thumbnail_slider' &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        // Preload & pause videos once the page is idle so first entry is instant.
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 0));
        idle(() => this.prewarmDeferredVideos());
      }

      /* ===================== Core events ===================== */

      onSlideChanged(event) {
        if (!event || !event.detail || !event.detail.currentElement) return;

        const current = event.detail.currentElement;

        // Sync the active thumbnail (if any)
        if (this.elements.thumbnails) {
          const thumb = this.elements.thumbnails.querySelector(
            `[data-target="${current.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumb);
        }

        // Load & (re)play when the slide becomes active
        this.playActiveMedia(current);
      }

      setActiveMedia(mediaId, prepend, scrollIntoView = true) {
        const activeMedia = this.elements.viewer?.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((el) => el.classList.remove('is-active'));

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          if (
            (!this.elements.thumbnails ||
              this.dataset.desktopLayout === 'stacked') &&
            scrollIntoView
          ) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);

        // Live region announce
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('[id^="Slide-"]')
          .forEach((el) => {
            el.classList.remove('is-active');
            const btn = el.querySelector('button');
            if (btn) btn.removeAttribute('aria-current');
          });

        thumbnail.classList.add('is-active');
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            theme.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        // retrigger onload to announce
        image.src = image.src;
      }

      /* ===================== Media control ===================== */

      prewarmDeferredVideos() {
        // Skip very slow connections if desired
        try {
          if (
            navigator.connection &&
            ['slow-2g', '2g'].includes(navigator.connection.effectiveType)
          ) {
            return;
          }
        } catch {}

        const medias = this.querySelectorAll('.deferred-media');

        medias.forEach((dm) => {
          if (!dm || dm.hasAttribute('loaded')) return;

          if (typeof dm.loadContent === 'function') {
            // Insert the real media under the poster
            dm.loadContent(false);
          } else {
            return;
          }

          // Once inserted, pause & prep for instant start
          queueMicrotask(() => {
            const video = dm.querySelector('video');
            if (!video) return;

            // Ensure later autoplay on mobile
            video.muted = true;
            video.playsInline = true;

            // Prevent playing now
            try {
              video.removeAttribute('autoplay');
            } catch {}
            try {
              video.preload = 'auto';
            } catch {}
            try {
              video.pause();
            } catch {}

            dm.dataset.prewarmed = '1';
          });
        });
      }

      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        const posterImg = deferredMedia.querySelector('img.deferred-media__poster');
        const posterBtn = deferredMedia.querySelector('button.deferred-media__poster');
        const hidePoster = () => {
          if (posterImg) posterImg.style.display = 'none';
          if (posterBtn) posterBtn.style.display = 'none';
        };

        const isLoaded = deferredMedia.hasAttribute('loaded');

        // First-time load (if not prewarmed for some reason)
        if (!isLoaded && typeof deferredMedia.loadContent === 'function') {
          hidePoster();
          deferredMedia.loadContent(false);
        }

        // HTML5 <video>
        const video = deferredMedia.querySelector('video');
        if (video) {
          hidePoster();
          video.muted = true;
          video.playsInline = true;

          const playNow = () => {
            // If you prefer always from start, uncomment next line:
            // try { video.currentTime = 0; } catch {}
            video.play().catch(() => {});
          };

          if (video.readyState >= 2) {
            playNow();
          } else {
            const onCanPlay = () => {
              video.removeEventListener('canplay', onCanPlay);
              playNow();
            };
            video.addEventListener('canplay', onCanPlay, { once: true });
            try {
              video.preload = 'auto';
            } catch {}
          }
          return;
        }

        // YouTube/Vimeo iframe
        const iframe = deferredMedia.querySelector('iframe');
        if (iframe) {
          hidePoster();
          try {
            const src = iframe.src || '';
            if (/youtube\.com|youtube-nocookie\.com/.test(src)) {
              // requires enablejsapi=1 in iframe URL
              iframe.contentWindow.postMessage(
                JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
                '*'
              );
            } else if (/player\.vimeo\.com/.test(src)) {
              iframe.contentWindow.postMessage(
                JSON.stringify({ method: 'play' }),
                '*'
              );
            }
          } catch {}
          return;
        }

        // <model-viewer>
        const model = deferredMedia.querySelector('model-viewer');
        if (model && typeof model.play === 'function') {
          hidePoster();
          model.play();
        }
      }

      /* ===================== Helpers ===================== */

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}


// gallery.js: 
// if (!customElements.get('media-gallery')) { customElements.define('media-gallery', class MediaGallery
// extends HTMLElement { constructor() { super();

// this.elements = { liveRegion: this.querySelector('[id^="GalleryStatus"]'), viewer:
// this.querySelector('[id^="GalleryViewer"]'), thumbnails: this.querySelector('[id^="GalleryThumbnails"]') } this.mql =
// window.matchMedia('(min-width: 750px)'); if (!this.elements.thumbnails) return;

// this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
// this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
// mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this,
// mediaToSwitch.dataset.target, false, true)); }); if (this.dataset.desktopLayout === 'thumbnail_slider' &&
// this.mql.matches) this.removeListSemantic(); }

// onSlideChanged(event) { if (event.detail.currentElement == undefined) return;

// const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId
// }"]`); this.setActiveThumbnail(thumbnail); }

// setActiveMedia(mediaId, prepend, scrollIntoView = true) { const activeMedia =
// this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
// this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => { element.classList.remove('is-active');
// }); activeMedia.classList.add('is-active');

// if (prepend) { activeMedia.parentElement.prepend(activeMedia); if (this.elements.thumbnails) { const activeThumbnail =
// this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
// activeThumbnail.parentElement.prepend(activeThumbnail); } if (this.elements.viewer.slider)
// this.elements.viewer.resetPages(); }

// this.preventStickyHeader(); window.setTimeout(() => { if (this.elements.thumbnails) {
// activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft }); } if ((!this.elements.thumbnails ||
// this.dataset.desktopLayout === 'stacked') && scrollIntoView) { activeMedia.scrollIntoView({behavior: 'smooth'}); } });
// this.playActiveMedia(activeMedia);

// if (!this.elements.thumbnails) return; const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${
// mediaId }"]`); this.setActiveThumbnail(activeThumbnail); this.announceLiveRegion(activeMedia,
// activeThumbnail.dataset.mediaPosition); }

// setActiveThumbnail(thumbnail) { if (!this.elements.thumbnails || !thumbnail) return;

// this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
// element.classList.remove('is-active'); element.querySelector('button').removeAttribute('aria-current'); });
// thumbnail.classList.add('is-active'); thumbnail.querySelector('button').setAttribute('aria-current', true);

// if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

// this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft }); }

// announceLiveRegion(activeItem, position) { const image = activeItem.querySelector('.product__modal-opener--image img');
// if (!image) return; image.onload = () => { this.elements.liveRegion.setAttribute('aria-hidden', false);
// this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace( '[index]', position );
// setTimeout(() => { this.elements.liveRegion.setAttribute('aria-hidden', true); }, 2000); }; image.src = image.src; }

// playActiveMedia(activeItem) { window.pauseAllMedia(); const deferredMedia = activeItem.querySelector('.deferred-media');
// if (deferredMedia) deferredMedia.loadContent(false); }

// preventStickyHeader() { this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header'); if
// (!this.stickyHeader) return; this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal')); }

// removeListSemantic() { if (!this.elements.viewer.slider) return; this.elements.viewer.slider.setAttribute('role',
// 'presentation'); this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation')); } }); }


